import { useState, useCallback } from 'react'
import { STRUCTURED_FILE_EXTS } from '@shared/types'
import { useApi } from '../contexts/ApiContext'

export const ALL_ACCEPTED_EXTS = ['pdf', 'docx', 'pptx', 'csv', 'xlsx', 'json']
const STRUCTURED_EXTS = new Set<string>(STRUCTURED_FILE_EXTS)

export interface ModalFile {
  name: string
  base64: string
}

/**
 * True for roster types with a client-side FTE headcount parser (xlsx/csv —
 * see parseStructuredFilesForHeadcount.ts). Deliberately excludes .json even
 * though it's accepted in ALL_ACCEPTED_EXTS: there is no client-side JSON FTE
 * extractor, so a JSON roster added here simply doesn't contribute to the
 * headcount reconciliation preview (PBI 2180 — known, accepted gap).
 */
export function isStructured(name: string): boolean {
  const ext = '.' + (name.split('.').pop()?.toLowerCase() ?? '')
  return STRUCTURED_EXTS.has(ext)
}

/** True for every roster type, JSON included — what the one-roster rule counts (WI 2269, PBI 2180). */
export function isRoster(name: string): boolean {
  return isStructured(name) || name.toLowerCase().endsWith('.json')
}

export function fileIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  if (ext === 'pdf') return '📄'
  if (ext === 'docx' || ext === 'pptx') return '📝'
  return '📊'
}

export function fileTypeLabel(name: string): { label: string; className: string } {
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  if (['pdf', 'docx', 'pptx'].includes(ext)) return { label: 'Document', className: 'bg-blue-50 text-blue-600' }
  if (['xlsx', 'csv', 'json'].includes(ext)) return { label: 'Roster', className: 'bg-green-50 text-green-600' }
  return { label: 'Other', className: 'bg-gray-100 text-gray-500' }
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary)
}

function mergeFiles(prev: ModalFile[], newEntries: ModalFile[]): ModalFile[] {
  const replaced = prev.map((f) => newEntries.find((e) => e.name === f.name) ?? f)
  const existingNames = new Set(prev.map((f) => f.name))
  const added = newEntries.filter((e) => !existingNames.has(e.name))
  return [...replaced, ...added]
}

export function useModalFiles(initialFiles: ModalFile[]) {
  const api = useApi()
  const [localFiles, setLocalFiles] = useState<ModalFile[]>(initialFiles)
  const [addError, setAddError] = useState<string | null>(null)

  const addFilesFromList = useCallback(async (incoming: FileList | File[]) => {
    const arr = Array.from(incoming)
    const valid = arr.filter((f) => {
      const ext = f.name.split('.').pop()?.toLowerCase() ?? ''
      return ALL_ACCEPTED_EXTS.includes(ext)
    })
    if (valid.length === 0) {
      setAddError('Unsupported file type. Accepted: PDF, DOCX, PPTX, CSV, XLSX, JSON.')
      return
    }
    setAddError(null)
    const newEntries: ModalFile[] = []
    for (const file of valid) {
      const base64 = arrayBufferToBase64(await file.arrayBuffer())
      newEntries.push({ base64, name: file.name })
    }
    setLocalFiles((prev) => mergeFiles(prev, newEntries))
  }, [])

  const openDialog = useCallback(async () => {
    try {
      const filePaths = await api.openFileDialog({
        properties: ['openFile', 'multiSelections'],
        filters: [
          { name: 'All Supported Files', extensions: ALL_ACCEPTED_EXTS },
          { name: 'Documents (AI-powered)', extensions: ['pdf', 'docx', 'pptx'] },
          { name: 'Structured Data', extensions: ['csv', 'xlsx', 'json'] },
        ],
      })
      if (!filePaths) return
      const paths = Array.isArray(filePaths) ? filePaths : [filePaths]
      const newEntries: ModalFile[] = []
      const errors: string[] = []
      for (const filePath of paths) {
        try {
          const { base64, name } = await api.readFile(filePath)
          const ext = name.split('.').pop()?.toLowerCase() ?? ''
          if (!ALL_ACCEPTED_EXTS.includes(ext)) { errors.push(`${name}: unsupported file type`); continue }
          newEntries.push({ base64, name })
        } catch (err) {
          errors.push(err instanceof Error ? err.message : String(err))
        }
      }
      if (newEntries.length > 0) setLocalFiles((prev) => mergeFiles(prev, newEntries))
      if (errors.length) setAddError(`Could not read: ${errors.join(', ')}`)
    } catch {
      // openFileDialog not available — no-op
    }
  }, [api])

  return { localFiles, setLocalFiles, addError, addFilesFromList, openDialog }
}
